import { z } from "zod";

/**
 * A site's subdomain. Stricter than the path on purpose: this is a DNS label
 * (RFC 1123), it physically cannot hold anything but letters, digits and
 * hyphens, and a label cannot start or end with a hyphen ("-hotel", "hotel-"
 * and "---" would produce an invalid hostname). property-service only checks
 * the length, so this pattern is the only gate.
 */
export const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * The `/property/{domainPath}` segment of a hotel inside a group.
 *
 * Hotel names carry ampersands and apostrophes ("Sleep Inn & Suites",
 * "Shepherd's Inn") and prod already serves paths with both, so they are
 * allowed. What is blocked is everything that breaks routing or could be
 * injected, which is what people actually put in here by mistake:
 *
 * - `/` and `:` — the path is concatenated into `Dns.subDomain` as
 *   `host/property/{path}` and parsed back by splitting on the first `/`, so a
 *   slash silently re-points the site. Blocking both also blocks a pasted
 *   `https://...`, the reported case.
 * - `%` — the lookup runs `decodeURIComponent` and falls back to the raw
 *   string when it throws, so a percent makes the same URL resolve two ways.
 * - `?` and `#` — truncate the path at the proxy.
 * - `<`, `>`, `"` and whitespace — script payloads and broken headers.
 * - non-ASCII — the path travels to the backend in the `x-path` HTTP header,
 *   which cannot carry it. This is why the one prod row with a curly
 *   apostrophe never resolved.
 */
export const SITE_PATH_PATTERN = /^[a-z0-9&'-]+$/;

/**
 * Curly quotes are visually identical to the straight ones and arrive by
 * copy-paste from Word or macOS. Rejecting them is unexplainable to whoever
 * typed the name, and storing them is impossible (see non-ASCII above), so
 * they are folded instead. Everything else is rejected rather than rewritten:
 * silently fixing a pasted URL teaches nothing.
 */
export const foldTypographicQuotes = (value: string) =>
	value.replace(/[‘’]/g, "'");

export const subDomainField = z
	.string()
	.trim()
	.toLowerCase()
	.min(1, { error: "val.required" })
	.max(15, { error: "val.max15" })
	.regex(SUBDOMAIN_PATTERN, { error: "val.subDomainInvalid" });

export const sitePathField = z
	.string()
	.trim()
	.toLowerCase()
	.transform(foldTypographicQuotes)
	.pipe(
		z
			.string()
			.min(1, { error: "val.required" })
			.max(150, { error: "val.max150" })
			.regex(SITE_PATH_PATTERN, { error: "val.sitePathInvalid" }),
	);

export const optionalSubDomainField = subDomainField.or(z.literal(""));
